import React, { useEffect, useMemo, useState } from "react";
import { accountAvatarImgSrc } from "../utils/accountAvatarUrl";
import defaultProfilePic from "../assets/default_profile_pic.jpeg";

function initialsFrom(emp, displayName) {
  if (emp?.initials) {
    const s = String(emp.initials).replace(/\s+/g, "").toUpperCase();
    if (s) return s.slice(0, 3);
  }
  const name = String(displayName || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (
        String(parts[0][0] || "") + String(parts[parts.length - 1][0] || "")
      ).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length >= 2) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length === 1) {
      return parts[0].toUpperCase();
    }
  }
  const fn = String(emp?.first_name || "").trim();
  const ln = String(emp?.last_name || "").trim();
  if (fn || ln) {
    return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase() || "?";
  }
  return "?";
}

/**
 * Round avatar: proxy URL when account id known; default profile picture on missing image.
 */
export default function StaffAvatarCircle({
  accountId,
  employee = null,
  name,
  avatarSrc,
  /** When false, skip /api/avatars proxy unless avatarSrc or legacy URL is set. */
  allowProxyFallback = true,
  className = "h-6 w-6",
  textClassName = "text-xs font-medium text-gray-700",
  emptyLabel = "NA",
  /** When set, clicking the photo opens a large preview (e.g. identifications table). */
  onImageClick,
}) {
  const [broken, setBroken] = useState(false);
  const emp = employee;

  const src = useMemo(() => {
    if (avatarSrc !== undefined) return avatarSrc || "";
    if (!emp) return "";
    const rid = accountId ?? emp.account_id ?? emp.accountId ?? emp.id;
    return accountAvatarImgSrc(rid, emp, {
      allowProxyWithoutMetadata: allowProxyFallback,
    });
  }, [emp, accountId, avatarSrc, allowProxyFallback]);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!emp && !accountId && !name) {
    return (
      <div
        className={`rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 ${className}`}
      >
        <span className={textClassName}>{emptyLabel}</span>
      </div>
    );
  }

  const displayName =
    name ??
    emp?.name ??
    [emp?.first_name, emp?.last_name].filter(Boolean).join(" ").trim() ??
    emp?.fullName ??
    emp?.username ??
    "";

  if (src && !broken) {
    const clickable = typeof onImageClick === "function";
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        className={`${className} rounded-full object-cover flex-shrink-0${
          clickable ? " cursor-pointer hover:opacity-80 transition-opacity" : ""
        }`}
        onClick={
          clickable
            ? (e) => {
                e.stopPropagation();
                onImageClick(src);
              }
            : undefined
        }
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onImageClick(src);
                }
              }
            : undefined
        }
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <img
      src={defaultProfilePic}
      alt={displayName || "Staff"}
      loading="lazy"
      className={`${className} rounded-full object-cover flex-shrink-0 border border-gray-200 bg-white`}
    />
  );
}
